import { Injectable } from '@angular/core';
import moment from 'moment';
import { lastValueFrom } from 'rxjs';
import { objectToCamel } from 'ts-case-convert';
import {
  IAvailabilities,
  IBooking,
  IBookingStop,
  IOrderedTerrainStop,
  IRouteDetails,
  IRouteReorganizationAssignment,
  IRouteReorganizationBookingResult,
  IRouteReorganizationDraft,
  IRouteReorganizationNewRoute,
  IRouteReorganizationRequest,
  IRouteReorganizationSimulation,
  IRouteReorganizationSourceSchedule,
  IRoutePreview,
  IServiceWizardSetup,
  IServiceWizardSetupPayload,
} from 'src/app/models';
import {
  ExpeditionType,
  ReservationFailureReason,
  RoutePreviewReason,
  RouteReorganizationType,
  TicketingType,
} from 'src/app/models/enums';
import { APP } from 'src/app/config/app';
import { AuthService } from '../auth/auth.service';
import { UtilsService } from '../utils/utils.service';
import {
  IOperationOutcome,
  WebSocketService,
} from '../web-socket/web-socket.service';
import { RouteReorganizationHttpService } from './route-reorganization.http.service';

export interface IRouteReorganizationNewRoutePlan {
  maxTime: number;
  secondsFromSourceStart: number;
  terrain?: any;
}

export interface IRouteReorganizationDepartureOption {
  newRouteIndex: number;
  departureDateTime: string;
}

@Injectable({
  providedIn: 'root',
})
export class RouteReorganizationService {
  public draft: IRouteReorganizationDraft | null = null;
  public returnUrl: string | null = null;

  constructor(
    private routeReorganizationHttpService: RouteReorganizationHttpService,
    private authService: AuthService,
    private webSocketService: WebSocketService,
    private utilsService: UtilsService,
  ) {}

  async simulate(
    serviceInfoId: number,
    request: IRouteReorganizationRequest,
  ): Promise<IRouteReorganizationSimulation> {
    const source$ = this.routeReorganizationHttpService.simulate(
      serviceInfoId,
      request,
    );
    const response = await lastValueFrom(source$);
    return {
      newRoutes: [],
      bookings: [],
      departures: [],
      unplannedBookingIds: [],
      cancelledBookingIds: [],
      restrictionWarnings: [],
      ...(objectToCamel(
        response as object,
      ) as unknown as Partial<IRouteReorganizationSimulation>),
    };
  }

  async apply(
    serviceInfoId: number,
    request: IRouteReorganizationRequest,
    onProgress?: (message: any) => void,
  ): Promise<IOperationOutcome> {
    const source$ = this.routeReorganizationHttpService.apply(
      serviceInfoId,
      request,
    );
    const operationId = await lastValueFrom(source$);
    // From here the backend is already applying it: losing the progress is not a failure of the reorganization
    try {
      const ephemeralToken = await this.ephemeralToken();
      const outcome = await this.webSocketService.trackOperation(
        operationId,
        ephemeralToken,
        onProgress,
        () => this.ephemeralToken(),
      );
      return outcome.error && !outcome.error.error_code
        ? { trackingLost: true }
        : outcome;
    } catch {
      return { trackingLost: true };
    }
  }

  private async ephemeralToken(): Promise<string> {
    return (await this.authService.getEphemeralToken()) as string;
  }

  isSelectionValid(type: RouteReorganizationType, routeIds: number[]) {
    return type === RouteReorganizationType.MERGE
      ? routeIds.length >= 2
      : routeIds.length === 1;
  }

  hasTicketing(routes: IRouteDetails[]): boolean {
    return routes.some(
      (route) =>
        !!route.ticketingType && route.ticketingType !== TicketingType.None,
    );
  }

  // The new route can only promise what every source route promised: the most fixed of their types
  expeditionTypeFor(routes: IRouteDetails[]): ExpeditionType | undefined {
    return routes
      .map((route) => route.expeditionType)
      .filter((type) =>
        APP.REORGANIZATION_EXPEDITION_TYPES_BY_FLEXIBILITY.includes(type),
      )
      .sort(
        (first, second) =>
          APP.REORGANIZATION_EXPEDITION_TYPES_BY_FLEXIBILITY.indexOf(first) -
          APP.REORGANIZATION_EXPEDITION_TYPES_BY_FLEXIBILITY.indexOf(second),
      )[0];
  }

  sameStop(first: IOrderedTerrainStop, second: IOrderedTerrainStop) {
    if (first.id === second.id) return true;
    return (
      this.utilsService.truncateCoordinate(first.latitude) ===
        this.utilsService.truncateCoordinate(second.latitude) &&
      this.utilsService.truncateCoordinate(first.longitude) ===
        this.utilsService.truncateCoordinate(second.longitude)
    );
  }

  // Every stop of every route once, keeping the order each route visits them in
  mergedStops(routes: IRouteDetails[]): IOrderedTerrainStop[] {
    const sequences = routes
      .map((route) => this.utilsService.orderedTerrainStops(route.terrain))
      .filter((stops) => stops.length)
      .sort((first, second) => second.length - first.length);
    if (!sequences.length) return [];
    const merged = [...sequences[0]];
    sequences.slice(1).forEach((sequence) => {
      let insertAt = 0;
      let pending: IOrderedTerrainStop[] = [];
      let sharesStops = false;
      sequence.forEach((stop) => {
        const index = merged.findIndex((candidate) =>
          this.sameStop(candidate, stop),
        );
        if (index < 0) {
          pending.push(stop);
          return;
        }
        sharesStops = true;
        const target = Math.max(index, insertAt);
        merged.splice(target, 0, ...pending);
        insertAt = Math.max(index + pending.length + 1, insertAt);
        pending = [];
      });
      merged.splice(sharesStops ? insertAt : merged.length, 0, ...pending);
    });
    return merged;
  }

  // Bookings usually share the destination (going to an event) or the origin (coming back), so the
  // stops where most passengers get on, or off, become direct routes of their own
  splitStops(
    route: IRouteDetails,
    bookings: IBooking[],
    count: number,
  ): IOrderedTerrainStop[][] {
    const stops = this.utilsService.orderedTerrainStops(route.terrain);
    const last = stops.length - 1;
    const stopIndex = (bookingStop?: IBookingStop) => {
      if (!bookingStop) return -1;
      const byId = stops.findIndex(
        (stop) => stop.id === bookingStop.serviceStopId,
      );
      if (byId >= 0) return byId;
      if (bookingStop.latitude != null && bookingStop.longitude != null) {
        const byPlace = stops.findIndex((stop) =>
          this.sameStop(stop, {
            id: NaN,
            name: '',
            latitude: bookingStop.latitude!,
            longitude: bookingStop.longitude!,
            secondsFromStart: 0,
          }),
        );
        if (byPlace >= 0) return byPlace;
      }
      const name = this.utilsService.normalizeStopName(bookingStop.name);
      return name
        ? stops.findIndex(
            (stop) => this.utilsService.normalizeStopName(stop.name) === name,
          )
        : -1;
    };
    const journeys = bookings
      .map((booking) => ({
        origin: stopIndex(booking.origin),
        destination: stopIndex(booking.destination),
        passengers: booking.seats || 1,
      }))
      .filter(
        ({ origin, destination }) =>
          origin >= 0 && destination >= 0 && origin < destination,
      );
    const passengersWhere = (matches: (journey: any) => boolean) =>
      journeys
        .filter(matches)
        .reduce((total, journey) => total + journey.passengers, 0);
    const towardsLast = passengersWhere(
      (journey) => journey.destination === last,
    );
    const fromFirst = passengersWhere((journey) => journey.origin === 0);
    if (stops.length - 1 < count || (!towardsLast && !fromFirst))
      return this.consecutiveStretches(stops, count);
    const goingToLast = towardsLast >= fromFirst;
    const commonEnd = goingToLast ? last : 0;
    const passengersAt = (index: number) =>
      passengersWhere((journey) =>
        goingToLast
          ? journey.origin === index && journey.destination === last
          : journey.destination === index && journey.origin === 0,
      );
    const candidates = stops
      .map((_, index) => index)
      .filter((index) => index !== commonEnd);
    const extracted = [...candidates]
      .sort(
        (first, second) =>
          passengersAt(second) - passengersAt(first) || first - second,
      )
      .slice(0, count - 1);
    const directRoute = (index: number) =>
      goingToLast ? [stops[index], stops[last]] : [stops[0], stops[index]];
    return [
      stops.filter((_, index) => !extracted.includes(index)),
      ...extracted.map(directRoute),
    ];
  }

  maxSplitRoutes(route: IRouteDetails): number {
    return Math.max(
      APP.REORGANIZATION_MIN_SPLIT_ROUTES,
      this.utilsService.orderedTerrainStops(route.terrain).length - 1,
    );
  }

  // Without a common origin or destination the route is cut into consecutive stretches sharing their ends
  private consecutiveStretches(
    stops: IOrderedTerrainStop[],
    count: number,
  ): IOrderedTerrainStop[][] {
    if (stops.length - 1 < count)
      return Array.from({ length: count }, () => stops);
    const boundaries = Array.from({ length: count + 1 }, (_, index) =>
      Math.round((index * (stops.length - 1)) / count),
    );
    return boundaries
      .slice(1)
      .map((end, index) => stops.slice(boundaries[index], end + 1));
  }

  secondsFromSourceStart(sourceRoute: IRouteDetails, newRouteTerrain: any) {
    const firstStop = this.utilsService.orderedTerrainStops(newRouteTerrain)[0];
    if (!firstStop) return 0;
    return (
      this.utilsService
        .orderedTerrainStops(sourceRoute.terrain)
        .find((stop) => this.sameStop(stop, firstStop))?.secondsFromStart ?? 0
    );
  }

  // When every source route ends at the same stop the passengers care about arriving on time;
  // otherwise about leaving on time
  anchorsOnArrival(routes: { terrain?: any }[]): boolean {
    const lastStops = routes.map((route) =>
      this.utilsService.orderedTerrainStops(route.terrain).at(-1),
    );
    return (
      lastStops.every(Boolean) &&
      lastStops.every((stop) => this.sameStop(stop!, lastStops[0]!))
    );
  }

  // Routes reaching the same stop arrive together; otherwise they keep when they leave
  arrivesTogether(
    draft: IRouteReorganizationDraft,
    newRoutes: { terrain?: any }[],
  ): boolean {
    return this.anchorsOnArrival(
      draft.type === RouteReorganizationType.MERGE
        ? draft.sourceSchedules.map((schedule) => schedule.route)
        : newRoutes,
    );
  }

  proposeSetups(
    draft: IRouteReorganizationDraft,
    newRoutes: IRouteReorganizationNewRoutePlan[],
    newRouteNames: string[],
  ): (IServiceWizardSetup | null)[] {
    const bookingsBySlot = this.bookingsBySlot(
      draft.simulation?.bookings ?? [],
    );
    const schedules = draft.sourceSchedules;
    const baseSetup = schedules.find((schedule) => schedule.setup)?.setup;
    if (!baseSetup && schedules.every((s) => !s.availabilities.length))
      return newRoutes.map(() => null);
    const arrivalAnchor = this.arrivesTogether(draft, newRoutes);
    return newRoutes.map((newRoute, newRouteIndex) => {
      const offsetMinutes = Math.round(newRoute.secondsFromSourceStart / 60);
      const rows = (field: 'daysOfWeek' | 'holyDays', key: string) =>
        draft.type === RouteReorganizationType.MERGE
          ? this.mergedRows(
              schedules,
              field,
              key,
              newRoute.maxTime,
              arrivalAnchor,
              bookingsBySlot,
            )
          : this.shiftedRows(
              schedules[0],
              field,
              newRoute.maxTime,
              offsetMinutes,
              arrivalAnchor,
            );
      const setupDetail = baseSetup
        ? {
            ...structuredClone(baseSetup),
            id: undefined,
            active: true,
            fromDate: moment
              .max(moment(), moment(baseSetup.fromDate))
              .format('YYYY-MM-DD'),
            toDate: this.latestToDate(schedules),
            route: { name: newRouteNames[newRouteIndex] },
            daysOfWeek: rows('daysOfWeek', 'dayOfWeek'),
            holyDays: rows('holyDays', 'holyDayType'),
          }
        : null;
      // Days of week already generate their departures: proposing them again would duplicate them
      const departures = setupDetail?.daysOfWeek?.length
        ? []
        : draft.type === RouteReorganizationType.MERGE
          ? this.mergedDepartures(
              schedules,
              newRoute.maxTime,
              arrivalAnchor,
              bookingsBySlot,
            )
          : this.shiftedDepartures(
              schedules[0],
              newRoute.maxTime,
              offsetMinutes,
              arrivalAnchor,
            );
      return {
        setupDetail,
        departures,
        showAvailabilitiesView:
          !setupDetail?.daysOfWeek?.length && !!departures.length,
      };
    });
  }

  private latestToDate(schedules: IRouteReorganizationSourceSchedule[]) {
    const toDates = schedules
      .map((schedule) => schedule.setup?.toDate)
      .filter(Boolean)
      .map((toDate) => moment(toDate));
    return (
      toDates.length ? moment.max(toDates) : moment().add(1, 'year')
    ).format('YYYY-MM-DD');
  }

  private bookingsBySlot(bookings: IRouteReorganizationBookingResult[]) {
    const counts = new Map<string, number>();
    bookings.forEach((booking) => {
      const departure = moment(booking.sourceDepartureDateTime);
      [
        this.slotKey(
          booking.sourceRouteId,
          APP.REORGANIZATION_WEEK_DAYS[departure.day()],
          departure.format('HH:mm'),
        ),
        this.slotKey(
          booking.sourceRouteId,
          departure.format(APP.REORGANIZATION_DATE_TIME_FORMAT),
        ),
      ].forEach((key) => counts.set(key, (counts.get(key) ?? 0) + 1));
    });
    return counts;
  }

  private slotKey(routeId: number | undefined, ...parts: string[]) {
    return [routeId, ...parts].join('|');
  }

  // Departures are paired by their order within the day, so the merged route runs as many as the busiest source
  private mergedRows(
    schedules: IRouteReorganizationSourceSchedule[],
    field: 'daysOfWeek' | 'holyDays',
    key: string,
    maxTime: number,
    arrivalAnchor: boolean,
    bookingsBySlot: Map<string, number>,
  ): any[] {
    const groups = new Map<string, { routeId: number; row: any }[][]>();
    schedules.forEach((schedule, scheduleIndex) => {
      (schedule.setup?.[field] ?? []).forEach((row: any) => {
        const dayGroups = groups.get(row[key]) ?? schedules.map(() => []);
        dayGroups[scheduleIndex].push({ routeId: schedule.route.id!, row });
        groups.set(row[key], dayGroups);
      });
    });
    return [...groups.values()].flatMap((dayGroups) => {
      dayGroups.forEach((group) =>
        group.sort(
          (first, second) =>
            this.toMinutes(first.row.startTime) -
            this.toMinutes(second.row.startTime),
        ),
      );
      const count = Math.max(...dayGroups.map((group) => group.length));
      return Array.from({ length: count }, (_, position) => {
        const pair = dayGroups.map((group) => group[position]).filter(Boolean);
        const startMinutes = arrivalAnchor
          ? Math.min(...pair.map(({ row }) => this.toMinutes(row.endTime))) -
            maxTime
          : Math.min(...pair.map(({ row }) => this.toMinutes(row.startTime)));
        const busiest = this.busiest(
          pair,
          ({ routeId, row }) =>
            this.slotKey(
              routeId,
              row.dayOfWeek ?? '',
              this.formatTime(this.toMinutes(row.startTime)),
            ),
          bookingsBySlot,
        );
        return this.scheduleRow(busiest.row, startMinutes, maxTime);
      });
    });
  }

  private shiftedRows(
    schedule: IRouteReorganizationSourceSchedule,
    field: 'daysOfWeek' | 'holyDays',
    maxTime: number,
    offsetMinutes: number,
    arrivalAnchor: boolean,
  ): any[] {
    return (schedule.setup?.[field] ?? []).map((row: any) =>
      this.scheduleRow(
        row,
        arrivalAnchor
          ? this.toMinutes(row.endTime) - maxTime
          : this.toMinutes(row.startTime) + offsetMinutes,
        maxTime,
      ),
    );
  }

  private scheduleRow(row: any, startMinutes: number, maxTime: number) {
    const withSeconds = String(row.startTime).length > 5;
    return {
      ...structuredClone(row),
      id: undefined,
      startTime: this.formatTime(startMinutes, withSeconds),
      endTime: this.formatTime(startMinutes + maxTime, withSeconds),
    };
  }

  private mergedDepartures(
    schedules: IRouteReorganizationSourceSchedule[],
    maxTime: number,
    arrivalAnchor: boolean,
    bookingsBySlot: Map<string, number>,
  ): any[] {
    const groups = new Map<
      string,
      { routeId: number; availability: IAvailabilities }[][]
    >();
    schedules.forEach((schedule, scheduleIndex) => {
      schedule.availabilities.forEach((availability) => {
        const date = moment(availability.startDateTime).format('YYYY-MM-DD');
        const dateGroups = groups.get(date) ?? schedules.map(() => []);
        dateGroups[scheduleIndex].push({
          routeId: schedule.route.id!,
          availability,
        });
        groups.set(date, dateGroups);
      });
    });
    return [...groups.values()].flatMap((dateGroups) => {
      dateGroups.forEach((group) =>
        group.sort(
          (first, second) =>
            moment(first.availability.startDateTime).valueOf() -
            moment(second.availability.startDateTime).valueOf(),
        ),
      );
      const count = Math.max(...dateGroups.map((group) => group.length));
      return Array.from({ length: count }, (_, position) => {
        const pair = dateGroups.map((group) => group[position]).filter(Boolean);
        const start = arrivalAnchor
          ? moment
              .min(
                pair.map(({ availability }) =>
                  moment(availability.endDateTime),
                ),
              )
              .subtract(maxTime, 'minutes')
          : moment.min(
              pair.map(({ availability }) =>
                moment(availability.startDateTime),
              ),
            );
        const busiest = this.busiest(
          pair,
          ({ routeId, availability }) =>
            this.slotKey(
              routeId,
              moment(availability.startDateTime).format(
                APP.REORGANIZATION_DATE_TIME_FORMAT,
              ),
            ),
          bookingsBySlot,
        );
        return this.departure(busiest.availability, start);
      });
    });
  }

  private shiftedDepartures(
    schedule: IRouteReorganizationSourceSchedule,
    maxTime: number,
    offsetMinutes: number,
    arrivalAnchor: boolean,
  ): any[] {
    return schedule.availabilities.map((availability) =>
      this.departure(
        availability,
        arrivalAnchor
          ? moment(availability.endDateTime).subtract(maxTime, 'minutes')
          : moment(availability.startDateTime).add(offsetMinutes, 'minutes'),
      ),
    );
  }

  private departure(availability: IAvailabilities, start: moment.Moment) {
    return {
      departureDateTime: start.format(APP.REORGANIZATION_DATE_TIME_FORMAT),
      vehicleId: availability.vehicle?.id,
      vehicleName: availability.vehicle?.name,
      driverId: availability.driver?.id,
      driverName: availability.driver?.name,
    };
  }

  private busiest<T>(
    pair: T[],
    slotOf: (member: T) => string,
    bookingsBySlot: Map<string, number>,
  ): T {
    return pair.reduce((busiest, member) =>
      (bookingsBySlot.get(slotOf(member)) ?? 0) >
      (bookingsBySlot.get(slotOf(busiest)) ?? 0)
        ? member
        : busiest,
    );
  }

  toMinutes(time: string | Date | undefined): number {
    const [hours, minutes] = String(time ?? '00:00')
      .split(':')
      .map(Number);
    return (hours || 0) * 60 + (minutes || 0);
  }

  private formatTime(totalMinutes: number, withSeconds = false): string {
    const minutesOfDay =
      ((totalMinutes % APP.MINUTES_PER_DAY) + APP.MINUTES_PER_DAY) %
      APP.MINUTES_PER_DAY;
    const hours = String(Math.floor(minutesOfDay / 60)).padStart(2, '0');
    const minutes = String(minutesOfDay % 60).padStart(2, '0');
    return withSeconds ? `${hours}:${minutes}:00` : `${hours}:${minutes}`;
  }

  departureOptions(
    setups: (IServiceWizardSetupPayload | null)[],
    newRouteIndexes: number[],
    date: string,
  ): IRouteReorganizationDepartureOption[] {
    const day = moment(date);
    const weekDay = APP.REORGANIZATION_WEEK_DAYS[day.day()];
    return newRouteIndexes
      .flatMap((newRouteIndex) => {
        const setup = setups[newRouteIndex];
        if (!setup) return [];
        const inRange =
          (!setup.fromDate || !day.isBefore(setup.fromDate, 'day')) &&
          (!setup.toDate || !day.isAfter(setup.toDate, 'day'));
        const fromDaysOfWeek = inRange
          ? (setup.daysOfWeek ?? [])
              .filter((row: any) => row.dayOfWeek === weekDay)
              .map(
                (row: any) =>
                  `${day.format('YYYY-MM-DD')}T${this.formatTime(this.toMinutes(row.startTime), true)}`,
              )
          : [];
        const fromDepartures = (setup.departures ?? [])
          .map((departure: any) => moment(departure.departureDateTime))
          .filter((departure) => departure.isSame(day, 'day'))
          .map((departure) =>
            departure.format(APP.REORGANIZATION_DATE_TIME_FORMAT),
          );
        return [...new Set([...fromDaysOfWeek, ...fromDepartures])].map(
          (departureDateTime) => ({
            newRouteIndex,
            departureDateTime,
          }),
        );
      })
      .sort(
        (first, second) =>
          first.departureDateTime.localeCompare(second.departureDateTime) ||
          first.newRouteIndex - second.newRouteIndex,
      );
  }

  // Each compatible booking goes to the closest departure of the same day; manual choices still offered are kept
  autoAssign(
    bookings: IRouteReorganizationBookingResult[],
    setups: (IServiceWizardSetupPayload | null)[],
    cancellations: number[],
    current: IRouteReorganizationAssignment[] = [],
  ): IRouteReorganizationAssignment[] {
    return bookings.flatMap((booking) => {
      if (
        cancellations.includes(booking.bookingId) ||
        !booking.compatibleNewRouteIndexes.length
      )
        return [];
      const options = this.departureOptions(
        setups,
        booking.compatibleNewRouteIndexes,
        booking.sourceDepartureDateTime,
      );
      const kept = current.find(
        (assignment) =>
          assignment.bookingId === booking.bookingId &&
          options.some(
            (option) =>
              option.newRouteIndex === assignment.newRouteIndex &&
              option.departureDateTime === assignment.departureDateTime,
          ),
      );
      if (kept) return [kept];
      const sourceDeparture = moment(booking.sourceDepartureDateTime).valueOf();
      const closest = options.reduce<
        IRouteReorganizationDepartureOption | undefined
      >(
        (best, option) =>
          !best ||
          Math.abs(
            moment(option.departureDateTime).valueOf() - sourceDeparture,
          ) <
            Math.abs(moment(best.departureDateTime).valueOf() - sourceDeparture)
            ? option
            : best,
        undefined,
      );
      return closest ? [{ bookingId: booking.bookingId, ...closest }] : [];
    });
  }

  incompatibleBookingIds(simulation: IRouteReorganizationSimulation): number[] {
    return simulation.bookings
      .filter((booking) => !booking.compatibleNewRouteIndexes.length)
      .map((booking) => booking.bookingId);
  }

  previewChanges(
    draft: IRouteReorganizationDraft,
    simulation: IRouteReorganizationSimulation,
  ): IRoutePreview[] {
    return draft.sourceSchedules
      .map(({ route }) => ({
        route: { id: route.id!, name: route.name! },
        ticketing: draft.ticketing,
        roundTrip: draft.roundTrip,
        bookings: simulation.bookings
          .filter(
            (booking) =>
              booking.sourceRouteId === route.id &&
              draft.cancellations.includes(booking.bookingId),
          )
          .sort((first, second) =>
            first.sourceDepartureDateTime.localeCompare(
              second.sourceDepartureDateTime,
            ),
          )
          .map((booking) => ({
            ...booking.booking,
            toCancel: true,
            reason: RoutePreviewReason.BOOKING_PATH_DELETED,
          })),
      }))
      .filter((preview) => preview.bookings.length);
  }

  // A route that grew longer keeps the edited departures; only the windows that no longer fit are widened
  stretchToMaxTime(
    setup: IServiceWizardSetup | null,
    maxTime: number,
    arrivalAnchor = false,
  ) {
    const rows = [
      ...(setup?.setupDetail?.daysOfWeek ?? []),
      ...(setup?.setupDetail?.holyDays ?? []),
    ];
    rows.forEach((row: any) => {
      if (this.windowMinutes(row) >= maxTime) return;
      const withSeconds = String(row.startTime).length > 5;
      if (arrivalAnchor)
        row.startTime = this.formatTime(
          this.toMinutes(row.endTime) - maxTime,
          withSeconds,
        );
      else
        row.endTime = this.formatTime(
          this.toMinutes(row.startTime) + maxTime,
          withSeconds,
        );
    });
  }

  windowMinutes(row: any): number {
    return this.utilsService.scheduleWindowMinutes(row.startTime, row.endTime);
  }

  // The new route is created exactly as left in the form; only the wizard helpers are dropped
  newRouteRequest(
    route: any,
    setup: IServiceWizardSetupPayload | null,
  ): IRouteReorganizationNewRoute {
    const { proposedStops, townStopsIncluded, ...routeDetail } = route;
    return {
      route: routeDetail,
      townStopsIncluded: townStopsIncluded ?? true,
      setup,
    };
  }

  // Related routes of every source route, except the source routes themselves that are deactivated
  relatedRoutesOf(routes: IRouteDetails[]) {
    const sourceRouteIds = routes.map((route) => route.id);
    const related = routes
      .flatMap((route) => route.relatedServices ?? [])
      .filter((relatedRoute) => !sourceRouteIds.includes(relatedRoute.id));
    return related.filter(
      (relatedRoute, index) =>
        related.findIndex((candidate) => candidate.id === relatedRoute.id) ===
        index,
    );
  }

  buildRequest(
    draft: IRouteReorganizationDraft,
    newRoutes: IRouteReorganizationNewRoute[],
    options: {
      recomputePrice: boolean;
      acceptedRestrictions: ReservationFailureReason[];
    } = {
      recomputePrice: false,
      acceptedRestrictions: [],
    },
  ): IRouteReorganizationRequest {
    return {
      type: draft.type,
      sourceRouteIds: draft.sourceSchedules.map(
        (schedule) => schedule.route.id!,
      ),
      newRoutes,
      assignments: draft.assignments,
      cancellations: draft.cancellations,
      cancellationOptions: draft.cancellationOptions,
      sendNotification: draft.sendNotification,
      ...options,
    };
  }
}
